import { createContext, useContext, type ReactNode } from "react";

// Conversation-scoped values that deep descendants need (e.g. artifact
// renderers inside markdown) but don't want to receive via prop drilling.
//
// Exactly one of `conversationId` or `shareToken` is populated:
//   - Owner / admin views set `conversationId` (UUID); artifact fetches use
//     the standard owner/dataset-access endpoint.
//   - Share-token views set `shareToken`; artifact fetches use the
//     token-scoped previews endpoint, with the shares-table row acting as
//     the authorization.
interface ConversationContextValue {
  conversationId: string | null;
  shareToken: string | null;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const ConversationProvider = ({
  conversationId,
  shareToken = null,
  children,
}: {
  conversationId: string | null;
  shareToken?: string | null;
  children: ReactNode;
}) => (
  <ConversationContext.Provider value={{ conversationId, shareToken }}>
    {children}
  </ConversationContext.Provider>
);

export const useConversationContext = (): ConversationContextValue => {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error("useConversationContext must be used within <ConversationProvider>");
  }
  return ctx;
};

export const useConversationId = (): string | null => useConversationContext().conversationId;
